import type { RosterEntry } from '@veriverse/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeLastActive, groupColleagues, type ColleagueRow } from '../state/colleagues.js';
import { loadRoster, openConversation } from '../net/dm.js';
import { useSessionStore } from '../state/session-store.js';

/** Relative times ("5m ago") are recomputed this often while the list is open. */
const TICK_MS = 30_000;

/** HUD box with the online count; expands into who is online and when the others were last here. */
export function ColleaguesBox() {
  const { t, i18n } = useTranslation();
  const onlineCount = useSessionStore((s) => s.onlineCount);
  const onlineIds = useSessionStore((s) => s.onlineUserIds);
  const me = useSessionStore((s) => s.user?.id);
  const [open, setOpen] = useState(false);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [now, setNow] = useState(() => new Date());
  const listId = useId();

  // Reload when opened and whenever someone joins or leaves, so "last active" is current.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadRoster()
      .then((entries) => {
        if (!cancelled) setRoster(entries);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, onlineIds]);

  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const timer = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(timer);
  }, [open]);

  const { online, offline } = groupColleagues(roster, onlineIds);

  const lastActive = (row: ColleagueRow): string => {
    const described = describeLastActive(row.lastActiveAt, now, i18n.language);
    return t(`hud.colleagues.${described.key}`, described);
  };

  const message = (userId: string) => {
    useSessionStore.getState().setDmOpen(true);
    void openConversation(userId);
    setOpen(false);
  };

  const messageButton = (row: ColleagueRow) =>
    row.userId === me ? null : (
      <button
        type="button"
        className="secondary colleague-message"
        aria-label={t('hud.colleagues.messageAria', { name: row.displayName })}
        onClick={() => message(row.userId)}
      >
        {t('hud.colleagues.message')}
      </button>
    );

  return (
    <div className="hud-box colleagues">
      <button
        type="button"
        className="colleagues-toggle"
        aria-expanded={open}
        aria-controls={listId}
        aria-live="polite"
        onClick={() => setOpen(!open)}
      >
        {t('hud.online', { count: onlineCount })}
        <span aria-hidden="true">{open ? ' ▴' : ' ▾'}</span>
      </button>
      {open && (
        <div id={listId} className="colleagues-list" aria-label={t('hud.colleagues.heading')}>
          <ColleagueSection heading={t('hud.colleagues.online')} count={online.length}>
            {online.map((row) => (
              <li key={row.userId}>
                <span className="colleague-dot online" aria-hidden="true" />
                <span className="colleague-name">{row.displayName}</span>
                {messageButton(row)}
                <span className="colleague-time">{t('hud.colleagues.activeNow')}</span>
              </li>
            ))}
          </ColleagueSection>
          <ColleagueSection heading={t('hud.colleagues.offline')} count={offline.length}>
            {offline.map((row) => (
              <li key={row.userId}>
                <span className="colleague-dot" aria-hidden="true" />
                <span className="colleague-name">{row.displayName}</span>
                {messageButton(row)}
                <span className="colleague-time">{lastActive(row)}</span>
              </li>
            ))}
          </ColleagueSection>
        </div>
      )}
    </div>
  );
}

function ColleagueSection(props: { heading: string; count: number; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <section>
      <h3>
        {props.heading} ({props.count})
      </h3>
      {props.count === 0 ? <p className="colleague-empty">{t('hud.colleagues.empty')}</p> : null}
      <ul>{props.children}</ul>
    </section>
  );
}
